import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { Lock, AlertCircle } from 'lucide-react';
import { AuthShell, AUTH_INPUT, AUTH_LABEL, AUTH_SUBMIT } from './AuthShell';

export function PasswordSetupPage() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const tenantId = searchParams.get('tenant_id');

  // Check for error in hash params
  const hashParams = new URLSearchParams(location.hash.slice(1));
  const error = hashParams.get('error');
  const errorCode = hashParams.get('error_code');
  const errorDescription = hashParams.get('error_description');
  const hasExpiredError = error === 'access_denied' && errorCode === 'otp_expired';

  useEffect(() => {
    // Verify we have the invitation context
    const checkInvitationContext = async () => {
      const { data: { session } } = await supabase.auth.getSession();

      // If user is already fully authenticated and has set password, redirect
      if (session?.user?.user_metadata?.password_set) {
        navigate('/');
      }
    };

    checkInvitationContext();
  }, [navigate]);

  const handlePasswordSetup = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast({
        title: t('passwordMismatchTitle'),
        description: t('passwordMismatchDescription'),
        variant: 'destructive',
      });
      return;
    }

    if (password.length < 8) {
      toast({
        title: t('passwordTooShortTitle'),
        description: t('passwordTooShortDescription'),
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      // Update the user's password and mark it as set
      const { error } = await supabase.auth.updateUser({
        password: password,
        data: {
          password_set: true,
          tenant_id: tenantId // Preserve tenant context
        }
      });

      if (error) throw error;

      toast({
        title: t('passwordSetSuccessTitle'),
        description: t('passwordSetSuccessDescription'),
      });

      // Redirect to the main application
      navigate('/');
    } catch (error) {
      console.error('Error setting password:', error);
      toast({
        title: t('passwordSetErrorTitle'),
        description: t('passwordSetErrorDescription'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      {hasExpiredError && (
        <div className="rounded-lg border border-red-300/30 bg-red-500/15 p-4 text-white">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <div className="space-y-2 text-sm">
              <p className="font-medium">{t('linkExpiredTitle')}</p>
              <p className="text-white/80">
                {errorDescription && errorDescription.toLowerCase().includes('expired')
                  ? t('linkExpiredDescription')
                  : errorDescription || t('linkExpiredDescription')}
              </p>
              <div className="flex flex-col gap-1">
                <Link to="/auth" className="font-medium underline underline-offset-4 hover:no-underline">
                  {t('goToLogin')}
                </Link>
                <Link to="/auth/reset-password" className="font-medium underline underline-offset-4 hover:no-underline">
                  {t('forgotPasswordLink')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handlePasswordSetup} className="space-y-4">
        <div className="space-y-3 text-center">
          <div className="flex justify-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10">
              <Lock className="h-6 w-6 text-white" />
            </span>
          </div>
          <div className="space-y-1.5">
            <h2 className="text-lg font-medium text-white">{t('setupPasswordTitle')}</h2>
            <p className="text-sm leading-relaxed text-white/60">{t('setupPasswordDescription')}</p>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="password" className={AUTH_LABEL}>{t('newPassword')}</Label>
          <Input
            id="password"
            type="password"
            placeholder={t('enterNewPassword')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={loading}
            minLength={8}
            className={AUTH_INPUT}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="confirmPassword" className={AUTH_LABEL}>{t('confirmPassword')}</Label>
          <Input
            id="confirmPassword"
            type="password"
            placeholder={t('confirmNewPassword')}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            disabled={loading}
            minLength={8}
            className={AUTH_INPUT}
          />
        </div>
        <p className="text-xs text-white/45">{t('passwordRequirements')}</p>
        <Button
          type="submit"
          className={AUTH_SUBMIT}
          disabled={loading || !password || !confirmPassword}
        >
          {loading ? t('settingPassword') : t('setPassword')}
        </Button>
      </form>
    </AuthShell>
  );
}
